/* eslint-disable @typescript-eslint/no-unused-vars -- Catch variables intentionally unused */
/**
 * Safe hydration strategy for Vue async components that handles dynamic keys
 *
 * Unlike Vue's built-in hydrateOnVisible, this version checks if DOM elements
 * are still connected before attempting hydration, preventing errors when
 * elements are removed due to key changes during rapid navigation.
 */

function elementIsVisibleInViewport(el: Element): boolean {
  const { top, left, bottom, right } = el.getBoundingClientRect()
  const { innerHeight, innerWidth } = window
  return (
    ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) &&
    ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
  )
}

export const hydrateOnVisibleSafe =
  (opts?: IntersectionObserverInit) =>
  (
    hydrate: () => void,
    forEach: (callback: (el: Element) => void | false) => void,
  ): (() => void) => {
    const ob = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue

        // Check if element is still connected to DOM before hydrating
        if (!e.target.isConnected) {
          // Element disconnected during intersection - stop observation
          ob.disconnect()
          return
        }

        try {
          ob.disconnect()
          hydrate()
          break
        } catch (_error) {
          // Hydration failed - continue processing other elements
          // Continue processing other elements instead of crashing
          break
        }
      }
    }, opts)

    forEach((el) => {
      if (!(el instanceof Element)) return

      // Check if element is connected before setting up observer
      if (!el.isConnected) {
        // Element not connected during setup - skip
        return false
      }

      if (elementIsVisibleInViewport(el)) {
        try {
          hydrate()
          ob.disconnect()
          return false
        } catch (_error) {
          // Immediate hydration failed - skip this element
          return false
        }
      }

      ob.observe(el)
      // Continue processing more elements (explicit return for TypeScript)
      return
    })

    return () => ob.disconnect()
  }
